import { History } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ErrorState, LoadingState } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { usePatientRecord } from '@/features/encounters/api';
import type { Allergy } from '@/features/patients/types';
import { dayLabel } from '@/pages/calendar/time';
import styles from './VisitHistoryPanel.module.css';

/** How many earlier visits the panel lists; the rest is on the full record. */
export const HISTORY_VISITS = 5;
const OPEN_KEY = 'ommri.consult.history-open';

function remembered(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function remember(open: boolean) {
  try {
    localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    /* a per-viewer convenience only */
  }
}

/**
 * RL-2185: the patient's earlier visits beside the consultation, so the doctor
 * need not leave the page. Reads the same audited record API as "Open the full
 * record" (GET /patients/:id/record), only after the panel is opened, and never
 * blocks the form. Without `encounters:read` the panel is not shown at all.
 */
export function VisitHistoryPanel({
  patientId,
  currentEncounterId,
  allergies,
}: {
  patientId: string;
  currentEncounterId: string;
  allergies: Pick<Allergy, 'id' | 'substance'>[];
}) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const [open, setOpen] = useState(remembered);
  const allowed = hasPermission('encounters:read');
  const { data, isLoading, error, refetch } = usePatientRecord(
    allowed && open ? patientId : undefined,
  );
  if (!allowed) return null;

  const visits = (data?.visits ?? [])
    .filter((v) => v.encounter && v.encounter.id !== currentEncounterId)
    .slice(0, HISTORY_VISITS);

  function toggle() {
    remember(!open);
    setOpen(!open);
  }

  return (
    <aside
      className={styles.panel}
      aria-label={t('consultation.history.title')}
    >
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        aria-controls="visit-history-body"
        onClick={toggle}
      >
        <History size={16} aria-hidden="true" />
        <span>
          {open
            ? t('consultation.history.hide')
            : t('consultation.history.show')}
        </span>
      </button>
      {open && (
        <div id="visit-history-body" className={styles.body}>
          <p className={styles.allergies}>
            <strong>{t('consultation.history.allergies')}:</strong>{' '}
            {allergies.length > 0
              ? allergies.map((a) => a.substance).join(', ')
              : t('consultation.history.noAllergies')}
          </p>
          {isLoading && (
            <LoadingState label={t('consultation.history.loading')} />
          )}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}
          {data && visits.length === 0 && (
            <p className={styles.muted}>{t('consultation.history.empty')}</p>
          )}
          {visits.length > 0 && (
            <ol className={styles.list}>
              {visits.map((v) => {
                const enc = v.encounter!;
                const dx = [...enc.diagnoses].sort(
                  (a, b) => Number(b.isPrimary) - Number(a.isPrimary),
                );
                return (
                  <li key={enc.id} className={styles.visit}>
                    <p className={styles.when}>
                      {dayLabel(v.date)} · {v.branch.name}
                    </p>
                    <p className={styles.muted}>
                      {t('consultation.history.doctor')}: {enc.doctor.fullName}
                      {enc.status === 'open' &&
                        ` · ${t('consultation.history.open')}`}
                    </p>
                    <p>
                      <strong>{t('consultation.history.diagnosis')}:</strong>{' '}
                      {dx.length > 0
                        ? dx.map((d) => `${d.code} ${d.description}`).join('; ')
                        : t('consultation.history.none')}
                    </p>
                    <p>
                      <strong>{t('consultation.history.medicines')}:</strong>{' '}
                      {enc.medicines && enc.medicines.length > 0
                        ? enc.medicines
                            .map((m) =>
                              [m.medicine, m.dose, m.frequency]
                                .filter(Boolean)
                                .join(' '),
                            )
                            .join('; ')
                        : t('consultation.history.none')}
                    </p>
                  </li>
                );
              })}
            </ol>
          )}
          <Link className={styles.link} to={`/patients/${patientId}`}>
            {t('consultation.history.fullRecord')}
          </Link>
        </div>
      )}
    </aside>
  );
}
